'use client';

import { useState, JSX, useEffect, useRef } from 'react';
import { tv } from 'tailwind-variants';
import { getTestProps } from 'lib/testing/utils';
import useDictionary from 'lib/hooks/useDictionary';
import { RemixIcon } from 'helpers/RemixIcon';
import SearchFacetTags from './SearchFacetTags';

// Custom SVG Checkbox Component
const CustomCheckbox = ({
  checked,
  onChange,
  className,
  testProps,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  testProps?: Record<string, string | number | boolean | undefined>;
}) => {
  return (
    <div className={className} {...testProps}>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="w-5 h-5 flex items-center justify-center transition-colors duration-150"
        aria-checked={checked}
        aria-label={checked ? 'Deselect filter' : 'Select filter'}
        role="checkbox"
      >
        {checked ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.46557 0.666504H12.5344C13.2052 0.666494 13.7589 0.666485 14.2098 0.703333C14.6783 0.741604 15.1088 0.823741 15.5133 1.02982C16.1405 1.34939 16.6504 1.85933 16.97 2.48654C17.1761 2.89098 17.2582 3.32157 17.2965 3.78998C17.3333 4.24097 17.3333 4.7946 17.3333 5.4654V12.5343C17.3333 13.2051 17.3333 13.7587 17.2965 14.2097C17.2582 14.6781 17.1761 15.1087 16.97 15.5131C16.6504 16.1403 16.1405 16.6503 15.5133 16.9699C15.1088 17.1759 14.6783 17.2581 14.2098 17.2963C13.7589 17.3332 13.2052 17.3332 12.5344 17.3332H5.46555C4.79475 17.3332 4.24113 17.3332 3.79014 17.2963C3.32172 17.2581 2.89113 17.1759 2.48669 16.9699C1.85948 16.6503 1.34955 16.1403 1.02997 15.5131C0.823893 15.1087 0.741756 14.6781 0.703485 14.2097C0.666637 13.7587 0.666646 13.2051 0.666657 12.5343V5.46542C0.666646 4.79461 0.666637 4.24098 0.703485 3.78998C0.741756 3.32157 0.823893 2.89098 1.02997 2.48654C1.34955 1.85933 1.85948 1.34939 2.48669 1.02982C2.89113 0.823741 3.32172 0.741604 3.79014 0.703333C4.24113 0.666485 4.79477 0.666494 5.46557 0.666504ZM3.92586 2.36446C3.56051 2.39431 3.37367 2.44842 3.24334 2.51483C2.92974 2.67462 2.67477 2.92958 2.51498 3.24319C2.44857 3.37352 2.39447 3.56036 2.36462 3.9257C2.33397 4.30078 2.33332 4.78602 2.33332 5.49984V12.4998C2.33332 13.2137 2.33397 13.6989 2.36462 14.074C2.39447 14.4393 2.44857 14.6262 2.51498 14.7565C2.67477 15.0701 2.92974 15.3251 3.24334 15.4848C3.37367 15.5513 3.56051 15.6054 3.92586 15.6352C4.30093 15.6659 4.78617 15.6665 5.49999 15.6665H12.5C13.2138 15.6665 13.6991 15.6659 14.0741 15.6352C14.4395 15.6054 14.6263 15.5513 14.7566 15.4848C15.0702 15.3251 15.3252 15.0701 15.485 14.7565C15.5514 14.6262 15.6055 14.4393 15.6354 14.074C15.666 13.6989 15.6667 13.2137 15.6667 12.4998V5.49984C15.6667 4.78602 15.666 4.30078 15.6354 3.9257C15.6055 3.56036 15.5514 3.37352 15.485 3.24319C15.3252 2.92958 15.0702 2.67462 14.7566 2.51483C14.6263 2.44842 14.4395 2.39431 14.0741 2.36446C13.6991 2.33382 13.2138 2.33317 12.5 2.33317H5.49999C4.78617 2.33317 4.30093 2.33382 3.92586 2.36446ZM13.3392 5.91058C13.6647 6.23602 13.6647 6.76366 13.3392 7.08909L8.33925 12.0891C8.01381 12.4145 7.48617 12.4145 7.16074 12.0891L4.66073 9.58909C4.3353 9.26366 4.3353 8.73602 4.66073 8.41058C4.98617 8.08515 5.51381 8.08515 5.83925 8.41058L7.74999 10.3213L12.1607 5.91058C12.4862 5.58515 13.0138 5.58515 13.3392 5.91058Z"
              fill="#0C1B39"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.46557 1.6665H13.5344C14.2052 1.66649 14.7589 1.66648 15.2098 1.70333C15.6783 1.7416 16.1088 1.82374 16.5133 2.02982C17.1405 2.34939 17.6504 2.85933 17.97 3.48654C18.1761 3.89098 18.2582 4.32157 18.2965 4.78998C18.3333 5.24097 18.3333 5.7946 18.3333 6.4654V13.5343C18.3333 14.2051 18.3333 14.7587 18.2965 15.2097C18.2582 15.6781 18.1761 16.1087 17.97 16.5131C17.6504 17.1403 17.1405 17.6503 16.5133 17.9699C16.1088 18.1759 15.6783 18.2581 15.2098 18.2963C14.7589 18.3332 14.2052 18.3332 13.5344 18.3332H6.46555C5.79475 18.3332 5.24113 18.3332 4.79014 18.2963C4.32172 18.2581 3.89113 18.1759 3.48669 17.9699C2.85948 17.6503 2.34955 17.1403 2.02997 16.5131C1.82389 16.1087 1.74176 15.6781 1.70349 15.2097C1.66664 14.7587 1.66665 14.2051 1.66666 13.5343V6.46542C1.66665 5.79461 1.66664 5.24098 1.70349 4.78998C1.74176 4.32157 1.82389 3.89098 2.02997 3.48654C2.34955 2.85933 2.85948 2.34939 3.48669 2.02982C3.89113 1.82374 4.32172 1.7416 4.79014 1.70333C5.24113 1.66648 5.79477 1.66649 6.46557 1.6665ZM4.92586 3.36446C4.56051 3.39431 4.37367 3.44842 4.24334 3.51483C3.92974 3.67462 3.67477 3.92958 3.51498 4.24319C3.44857 4.37352 3.39447 4.56036 3.36462 4.9257C3.33397 5.30078 3.33332 5.78602 3.33332 6.49984V13.4998C3.33332 14.2137 3.33397 14.6989 3.36462 15.074C3.39447 15.4393 3.44857 15.6262 3.51498 15.7565C3.67477 16.0701 3.92974 16.3251 4.24334 16.4848C4.37367 16.5513 4.56051 16.6054 4.92586 16.6352C5.30093 16.6659 5.78617 16.6665 6.49999 16.6665H13.5C14.2138 16.6665 14.6991 16.6659 15.0741 16.6352C15.4395 16.6054 15.6263 16.5513 15.7566 16.4848C16.0702 16.3251 16.3252 16.0701 16.485 15.7565C16.5514 15.6262 16.6055 15.4393 16.6354 15.074C16.666 14.6989 16.6667 14.2137 16.6667 13.4998V6.49984C16.6667 5.78602 16.666 5.30078 16.6354 4.9257C16.6055 4.56036 16.5514 4.37352 16.485 4.24319C16.3252 3.92958 16.0702 3.67462 15.7566 3.51483C15.6263 3.44842 15.4395 3.39431 15.0741 3.36446C14.6991 3.33382 14.2138 3.33317 13.5 3.33317H6.49999C5.78617 3.33317 5.30093 3.33382 4.92586 3.36446Z"
              fill="#0C1B39"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export type FacetValue = {
  id: string;
  text: string;
  count?: number;
  selected?: boolean;
};

export type Facet = {
  name: string;
  label: string;
  values: FacetValue[];
};

export type SearchFacetFiltersMobileProps = {
  facets: Facet[];
  selectedFacets?: Array<{
    label: string;
    name: string;
    value: Array<{
      id: string;
      text: string;
      count?: number;
    }>;
  }>;
  onFacetToggle?: (
    facetName: string,
    valueId: string,
    selected: boolean,
    valueText: string
  ) => void;
  onClearAll?: () => void;
  showClearAll?: boolean;
  className?: string;
};

const SearchFacetFiltersMobile = ({
  facets,
  selectedFacets = [],
  onFacetToggle,
  onClearAll,
  showClearAll = false,
}: SearchFacetFiltersMobileProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [expandedFacets, setExpandedFacets] = useState<string[]>([]);
  const [headerHeight, setHeaderHeight] = useState(64); // Default to 64px
  const overlayRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      const header = document.getElementById('header');
      if (header) {
        const height = header.getBoundingClientRect().bottom;
        setHeaderHeight(height);
      }
    };

    // Calculate on mount and when overlay opens
    calculateHeaderHeight();

    // Recalculate when overlay opens to catch any dynamic changes (e.g., alert component)
    let timeoutId: NodeJS.Timeout | null = null;
    if (isOverlayOpen) {
      // Small delay to ensure DOM is updated
      timeoutId = setTimeout(calculateHeaderHeight, 100);
    }

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeaderHeight);

    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOverlayOpen]);

  // Lock body scroll when overlay is open
  useEffect(() => {
    // Store original overflow value before any changes
    const originalOverflow = window.getComputedStyle(document.body).overflow;

    if (isOverlayOpen) {
      // Prevent background scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll when overlay closes
      document.body.style.overflow = originalOverflow;
    }

    // Cleanup: always restore on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOverlayOpen]);

  const toggleOverlay = () => {
    setIsOverlayOpen(!isOverlayOpen);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
  };

  // Close overlay when clicking outside (check both button and overlay)
  useEffect(() => {
    if (!isOverlayOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickOnButton = buttonRef.current?.contains(target);
      const isClickOnOverlay = overlayRef.current?.contains(target);

      if (!isClickOnButton && !isClickOnOverlay) {
        closeOverlay();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOverlayOpen]);

  const toggleFacetExpansion = (facetName: string) => {
    setExpandedFacets((prev) =>
      prev.includes(facetName) ? prev.filter((name) => name !== facetName) : [...prev, facetName]
    );
  };

  const handleFacetToggle = (
    facetName: string,
    valueId: string,
    selected: boolean,
    valueText: string
  ) => {
    onFacetToggle?.(facetName, valueId, selected, valueText);
  };

  const {
    base,
    filterButton,
    filterButtonText,
    filterButtonIcon,
    overlay,
    overlayContent,
    overlayCloseIcon,
    overlayHeader,
    overlayTitle,
    closeButton,
    overlayBody,
    clearAllButton,
    SearchFacetTagsWrapper,
    facetWrapper,
    facetItemWrapper,
    facetHead,
    facetHeadIcon,
    facetItemBlock,
    facetItem,
    headerText,
    itemLabel,
  } = TAILWIND_VARIANTS();

  return (
    <>
      {/* Mobile Filter Button */}
      <div ref={buttonRef} className={base()}>
        <button
          onClick={toggleOverlay}
          className={filterButton()}
          aria-label={getDictionaryValue('Filters') || 'Filters'}
          {...getTestProps('mobile-filter-button')}
        >
          <RemixIcon name="ri-filter-3-line" className={filterButtonIcon()} />
          <span className={filterButtonText()}>{getDictionaryValue('Filters')}</span>
        </button>
      </div>

      {/* Overlay */}
      {isOverlayOpen && (
        <div ref={overlayRef} className={overlay()} style={{ marginTop: `${headerHeight}px` }}>
          <div className={overlayContent()}>
            {/* Overlay Header */}
            <div className={overlayCloseIcon()}>
              <button
                onClick={closeOverlay}
                className={closeButton()}
                aria-label="Close filters"
                {...getTestProps('close-filters-overlay')}
              >
                <RemixIcon name="ri-close-line" />
              </button>
            </div>
            <div className={overlayHeader()}>
              <h3 className={overlayTitle()}>{getDictionaryValue('Filters')}</h3>
              {showClearAll && (
                <button
                  onClick={onClearAll}
                  className={clearAllButton()}
                  {...getTestProps('clear-all-filters')}
                  aria-label={getDictionaryValue('ClearAll') || 'Clear all filters'}
                >
                  {getDictionaryValue('ClearAll')}
                </button>
              )}
            </div>

            {/* Overlay Body */}
            <div className={overlayBody()}>
              {/* Selected Facet Tags */}
              <div className={SearchFacetTagsWrapper()}>
                <SearchFacetTags
                  selectedFacets={selectedFacets}
                  onRemoveTag={(facetName, valueId, valueText) =>
                    onFacetToggle?.(facetName, valueId, false, valueText)
                  }
                  onClearAll={onClearAll || (() => {})}
                  showClearAll={showClearAll}
                />
              </div>

              {/* Facet List */}
              <div className={facetWrapper()}>
                {facets.map((facet) => (
                  <div key={facet.name} className={facetItemWrapper()}>
                    <button
                      onClick={() => toggleFacetExpansion(facet.name)}
                      className={facetHead({ isExpanded: expandedFacets.includes(facet.name) })}
                      aria-label={facet.label}
                      {...getTestProps(`facet-${facet.name}`)}
                    >
                      <span className={headerText()}>{facet.label}</span>
                      <RemixIcon
                        name={
                          expandedFacets.includes(facet.name)
                            ? 'ri-arrow-up-s-line'
                            : 'ri-arrow-down-s-line'
                        }
                        className={facetHeadIcon()}
                      />
                    </button>

                    {expandedFacets.includes(facet.name) && (
                      <div className={facetItemBlock()}>
                        {facet.values.map((value) => (
                          <div key={value.id} className={facetItem()}>
                            <CustomCheckbox
                              checked={value.selected || false}
                              onChange={(checked) =>
                                handleFacetToggle(facet.name, value.id, checked, value.text)
                              }
                              testProps={getTestProps(`facet-value-${value.id}`)}
                            />
                            <span
                              className={itemLabel()}
                              onClick={() =>
                                handleFacetToggle(
                                  facet.name,
                                  value.id,
                                  !(value.selected || false),
                                  value.text
                                )
                              }
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleFacetToggle(
                                    facet.name,
                                    value.id,
                                    !(value.selected || false),
                                    value.text
                                  );
                                }
                              }}
                              aria-label={`${value.selected ? 'Unselect' : 'Select'} ${value.text}`}
                            >
                              {value.text} {value.count !== undefined && `(${value.count})`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchFacetFiltersMobile;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['lg:hidden', 'block', 'w-fit'],
    filterButton: [
      'flex',
      'flex-row',
      'items-center',
      'justify-between',
      'w-fit',
      'py-spacing-spacing-4 sm:py-spacing-spacing-16',
      'px-spacing-spacing-8 sm:px-spacing-spacing-24',
      'gap-spacing-spacing-8',
      'bg-component-button-primary-bg',
      'border-general-border-width-button',
      'border-component-button-primary-border',
      'rounded-general-border-radius-button',
      'h-11',
      'hover:bg-component-button-primary-bg-hover',
      'hover:border-component-button-primary-border-hover',
      'hover:text-component-button-primary-icon-hover',
    ],
    filterButtonIcon: ['w-4', 'h-4', 'text-component-button-primary-inverse-icon'],
    filterButtonText: [
      'w-fit',
      'flex',
      'items-center',
      'justify-center',
      'text-component-button-primary-inverse-text',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-xxsmall',
      'font-semibold',
    ],

    overlay: [
      'fixed',
      'inset-0',
      'z-50',
      'flex',
      'flex-col',
      'bg-color-grayscale-white',
      'pt-spacing-spacing-16',
      'lg:hidden',
    ],
    overlayContent: [
      'flex',
      'flex-col',
      'h-full',
      'bg-color-grayscale-white',
      'py-spacing-spacing-0',
      'px-spacing-spacing-8',
    ],
    overlayCloseIcon: ['w-full', 'h-6', 'flex', 'items-center', 'justify-end'],
    closeButton: ['flex', 'items-center', 'justify-center', 'w-6', 'h-6', 'text-p-color-icon'],
    overlayHeader: [
      'flex',
      'justify-between',
      'items-center',
      'bg-p-color-bg-surface',
      'pl-spacing-spacing-16',
      'pr-spacing-spacing-0',
      'py-spacing-spacing-8',
    ],
    overlayTitle: [
      'text-color-text-dark',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'leading-typography-line-height-body-medium',
    ],
    clearAllButton: [
      'text-color-text-dark',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-normal',
      'underline',
    ],
    overlayBody: ['flex-1', 'overflow-y-auto'],
    SearchFacetTagsWrapper: [
      'py-spacing-spacing-8',
      'px-spacing-spacing-10',
      'w-full',
      'flex',
      'flex-row',
      'gap-spacing-spacing-8',
      'justify-start',
      'items-center',
    ],
    facetWrapper: ['w-full', 'flex', 'flex-col'],
    facetItemWrapper: ['w-full', 'flex', 'flex-col', 'py-spacing-spacing-4'],
    facetHead: [
      'w-full',
      'py-spacing-spacing-8',
      'px-spacing-spacing-12',
      'flex',
      'items-center',
      'justify-between',
      'rounded-border-radius-radius-1',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'font-normal',
      'leading-typography-line-height-body-medium',
      'bg-p-color-bg-surface',
      'focus:outline-none',
    ],
    facetHeadIcon: ['transition-transform', 'duration-200', 'text-p-color-icon'],
    facetItemBlock: [
      'w-full',
      'flex',
      'flex-col',
      'max-h-[160px] md:max-h-[300px]',
      'overflow-y-auto',
    ],
    facetItem: [
      'w-full',
      'flex',
      'items-center',
      'py-spacing-spacing-8',
      'px-spacing-spacing-12',
      'gap-spacing-spacing-8',
    ],
    headerText: [],
    itemLabel: [
      'text-p-color-text',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'font-normal',
      'leading-typography-line-height-body-medium',
      'hover:text-p-color-text',
      'hover:underline',
      'cursor-pointer',
      'transition-colors',
      'duration-150',
    ],
  },
  variants: {
    isExpanded: {
      true: {
        facetHead: ['bg-color-core-color-4-SkyBlue', 'hover:bg-color-core-color-4-SkyBlue'],
      },
      false: {
        facetHead: ['bg-p-color-bg-surface', 'hover:bg-color-core-color-4-skyblue-lightTint'],
      },
    },
  },
  defaultVariants: {
    isExpanded: false,
  },
});
